import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ref, get, set } from 'firebase/database';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { useTestMode } from './TestModeContext';
import {
  Currency,
  formatPrice as formatPriceUtil,
  formatPricePerToken as formatPricePerTokenUtil,
  convertCurrency,
  getCurrencySymbol,
  CURRENCY_SYMBOLS,
} from '../utils/formatCurrency';

interface CurrencyContextType {
  currency: Currency;
  setCurrency: (currency: Currency) => Promise<void>;
  formatPrice: (amountInPence: number, options?: { showDecimals?: boolean }) => string;
  formatPricePerToken: (pricePerTokenGbpPence: number) => string;
  convertToSelectedCurrency: (amountGbpPence: number) => number;
  currencySymbol: string;
  availableCurrencies: Currency[];
  loading: boolean;
}

const CurrencyContext = createContext<CurrencyContextType | null>(null);

const AVAILABLE_CURRENCIES: Currency[] = ['GBP', 'EUR', 'USD'];
const DEFAULT_CURRENCY: Currency = 'GBP';
const CURRENCY_PREF_KEY = 'userPreferences/currency';

/**
 * Provides currency preference and formatting helpers to the component tree.
 * Persists the user's chosen currency to Firebase and converts GBP-based
 * prices at display time.
 */
export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currency, setCurrencyState] = useState<Currency>(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { testMode } = useTestMode();

  // Load user's currency preference from Firebase
  useEffect(() => {
    const loadCurrencyPreference = async () => {
      if (!user) {
        // Reset to default for unauthenticated users
        setCurrencyState(DEFAULT_CURRENCY);
        setLoading(false);
        return;
      }

      try {
        const basePath = `user_data/${user.uid}/${CURRENCY_PREF_KEY}`;
        const path = testMode ? `testMode/${basePath}` : basePath;
        const snapshot = await get(ref(db, path));

        if (snapshot.exists()) {
          const savedCurrency = snapshot.val() as Currency;
          if (AVAILABLE_CURRENCIES.includes(savedCurrency)) {
            setCurrencyState(savedCurrency);
          }
        }
      } catch (error) {
        console.warn('[CurrencyContext] Failed to load currency preference:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCurrencyPreference();
  }, [user, testMode]);

  // Save currency preference to Firebase
  const setCurrency = useCallback(async (newCurrency: Currency) => {
    setCurrencyState(newCurrency);

    if (!user) return;

    try {
      const basePath = `user_data/${user.uid}/${CURRENCY_PREF_KEY}`;
      const path = testMode ? `testMode/${basePath}` : basePath;
      await set(ref(db, path), newCurrency);
    } catch (error) {
      console.warn('[CurrencyContext] Failed to save currency preference:', error);
    }
  }, [user, testMode]);

  // Format price using current currency
  const formatPrice = useCallback((amountInPence: number, options?: { showDecimals?: boolean }) => {
    return formatPriceUtil(amountInPence, currency, {
      showDecimals: options?.showDecimals ?? true,
      convert: true,
    });
  }, [currency]);

  // Format price per token
  const formatPricePerToken = useCallback((pricePerTokenGbpPence: number) => {
    return formatPricePerTokenUtil(pricePerTokenGbpPence, currency);
  }, [currency]);

  // Convert amount to selected currency
  const convertToSelectedCurrency = useCallback((amountGbpPence: number) => {
    return convertCurrency(amountGbpPence, currency);
  }, [currency]);

  // Get current currency symbol
  const currencySymbol = CURRENCY_SYMBOLS[currency];

  const value: CurrencyContextType = {
    currency,
    setCurrency,
    formatPrice,
    formatPricePerToken,
    convertToSelectedCurrency,
    currencySymbol,
    availableCurrencies: AVAILABLE_CURRENCIES,
    loading,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

/**
 * Consume the CurrencyContext. Must be used within a CurrencyProvider.
 *
 * @returns Currency state, setter, and formatting helpers.
 */
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
